/**
 * #935 — what this prompt cost, shown under the assistant message.
 *
 * A prompt fans out into many billed operations: model turns (billed on tokens)
 * and tool calls (billed per call). Those are two genuinely different billing
 * mechanisms, so the split is measured rather than apportioned.
 *
 * Phase 2 adds the per-group expansion. Groups are by tool *family*, not by
 * individual tool: `callRecorder` records a tool name but the report and
 * integration billing paths record only a family, so per-tool detail would mean
 * inventing precision the data does not have.
 */
import React, { useState } from 'react'

export type CreditSummary = {
  total: number
  model: number
  tools: number
  toolCalls: number
  durationMs?: number
  byType?: Array<{ type: string; credits: number; count: number }>
  groups?: Array<{ label: string; credits: number; count: number }>
}

/**
 * Credits are stored at 4 dp but usually read as money, so show 2 dp — except
 * below a cent, where 2 dp would render a real charge as "0.00" (the defect
 * fixed for the admin view in #876). Mirrors `fmtCreditsPrecise` there.
 */
export const formatCredits = (value: number): string => {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '0'
  return n < 0.01
    ? n.toLocaleString(undefined, { maximumFractionDigits: 4 })
    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const MessageCreditCost: React.FC<{
  summary?: CreditSummary | null
  /** Applied to the root. Passed in rather than wrapped by the caller so that a
   *  turn with no cost renders nothing at all — an empty `ml-auto` wrapper still
   *  absorbs flex space and shifts whatever follows it. */
  className?: string
}> = ({ summary, className }) => {
  const [expanded, setExpanded] = useState(false)

  const total = Number(summary?.total)
  // A free turn has nothing worth saying; "0 credits" is just noise.
  if (!summary || !Number.isFinite(total) || total <= 0) return null

  const model = Number(summary.model) || 0
  const tools = Number(summary.tools) || 0
  const toolCalls = Number(summary.toolCalls) || 0
  const groups = Array.isArray(summary.groups) ? summary.groups : []

  // Only worth splitting when both sides actually cost something.
  const showSplit = model > 0 && tools > 0
  // A single group is already fully described by the summary line.
  const canExpand = groups.length > 1

  return (
    <div className={`ai-chat-message-cost${className ? ` ${className}` : ''}`}>
      <span
        className={canExpand ? 'ai-chat-message-cost-toggle' : undefined}
        onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
        title={canExpand ? 'Show cost breakdown' : 'Credits charged for this prompt'}
      >
        {formatCredits(total)} credits
        {showSplit && (
          <span className="ai-chat-message-cost-detail">
            {' · '}{formatCredits(model)} model, {formatCredits(tools)} tools
            {toolCalls > 0 && ` (${toolCalls} call${toolCalls === 1 ? '' : 's'})`}
          </span>
        )}
        {canExpand && <span className="ai-chat-message-cost-caret">{expanded ? ' ▴' : ' ▾'}</span>}
      </span>

      {canExpand && expanded && (
        <div className="ai-chat-message-cost-breakdown">
          {groups.map((g) => (
            <div key={g.label} className="ai-chat-message-cost-row">
              <span>
                {g.label}
                <span className="ai-chat-message-cost-count">
                  {' '}× {g.count}
                </span>
              </span>
              <span>{formatCredits(g.credits)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MessageCreditCost
